/**
 * Cursor — a dot and a trailing ring. Ink on paper, paper at night; the
 * ring fills red and reads "View" over a shop. Magnetic buttons lean
 * towards the pointer. Mouse only.
 *
 * @module chrome/cursor
 */

import { REDUCED_MOTION, TOUCH, cssColor } from '../config.js';
import { $, $$ } from '../lib/dom.js';
import { gsap } from '../lib/gsap.js';

// The back door of the footer stays a door nobody notices: the ring does not grow on it.
const INTERACTIVE = 'a:not([data-door]), button, input, textarea, [data-hover]';
const VIEWABLE = '[data-card], [data-shop]';

export function animateCursor() {
    const cursor = $('.cursor');

    if (!cursor || TOUCH || REDUCED_MOTION) {
        return;
    }

    const dot = $('.cursor__dot', cursor);
    const ring = $('.cursor__ring', cursor);
    const label = $('.cursor__label', cursor);
    const red = cssColor('red', '#ea1d0f');

    const dotX = gsap.quickTo(dot, 'x', { duration: 0.12, ease: 'power3' });
    const dotY = gsap.quickTo(dot, 'y', { duration: 0.12, ease: 'power3' });
    const ringX = gsap.quickTo(ring, 'x', { duration: 0.5, ease: 'power3' });
    const ringY = gsap.quickTo(ring, 'y', { duration: 0.5, ease: 'power3' });

    window.addEventListener(
        'mousemove',
        ({ clientX, clientY }) => {
            dotX(clientX);
            dotY(clientY);
            ringX(clientX);
            ringY(clientY);
        },
        { passive: true },
    );

    // The ring colour is set inline while it grows, so the night variant
    // is picked here rather than in the stylesheet.
    const ringLine = () => (cursor.classList.contains('is-night') ? 'rgba(244,241,234,.45)' : 'rgba(22,19,15,.35)');
    let labelled = false;

    /** @param {string} text - Empty for a plain link. */
    const grow = (text) => {
        labelled = Boolean(text);
        gsap.to(ring, {
            scale: text ? 1.15 : 1,
            backgroundColor: text ? red : 'rgba(0,0,0,0)',
            borderColor: text ? red : ringLine(),
            duration: 0.4,
            overwrite: 'auto',
        });
        gsap.to(dot, { scale: text ? 0 : 0.6, duration: 0.3, overwrite: 'auto' });
        label.textContent = text;
        gsap.to(label, { opacity: text ? 1 : 0, duration: 0.25, overwrite: 'auto' });
    };

    const shrink = () => {
        labelled = false;
        gsap.to(ring, {
            scale: 0.6,
            backgroundColor: 'rgba(0,0,0,0)',
            borderColor: ringLine(),
            duration: 0.4,
            overwrite: 'auto',
        });
        gsap.to(dot, { scale: 1, duration: 0.3, overwrite: 'auto' });
        gsap.to(label, { opacity: 0, duration: 0.2, overwrite: 'auto' });
    };

    document.addEventListener('mouseover', ({ target }) => {
        if (!(target instanceof Element)) {
            return;
        }

        const night = Boolean(target.closest('[data-night]'));

        if (night !== cursor.classList.contains('is-night')) {
            cursor.classList.toggle('is-night', night);

            if (!labelled) {
                gsap.to(ring, { borderColor: ringLine(), duration: 0.3, overwrite: 'auto' });
            }
        }

        if (target.closest(VIEWABLE)) {
            grow('View');
        } else if (target.closest(INTERACTIVE)) {
            grow('');
        }
    });

    document.addEventListener('mouseout', ({ target }) => {
        if (target instanceof Element && target.closest(`${INTERACTIVE}, ${VIEWABLE}`)) {
            shrink();
        }
    });
}

/** Buttons and signposts that lean towards the pointer. */
export function animateMagnetic() {
    if (TOUCH || REDUCED_MOTION) {
        return;
    }

    $$('[data-magnetic]').forEach((element) => {
        const toX = gsap.quickTo(element, 'x', { duration: 0.5, ease: 'power3' });
        const toY = gsap.quickTo(element, 'y', { duration: 0.5, ease: 'power3' });

        element.addEventListener('mousemove', ({ clientX, clientY }) => {
            const box = element.getBoundingClientRect();
            toX((clientX - (box.left + box.width / 2)) * 0.2);
            toY((clientY - (box.top + box.height / 2)) * 0.3);
        });

        element.addEventListener('mouseleave', () => {
            toX(0);
            toY(0);
        });

        // Outlined buttons fill from below on hover.
        const fill = $('.button__fill', element);

        if (fill && !element.classList.contains('button--solid')) {
            element.addEventListener('mouseenter', () =>
                gsap.to(fill, { yPercent: -100, duration: 0.5, ease: 'power3.out' }),
            );
            element.addEventListener('mouseleave', () =>
                gsap.to(fill, { yPercent: 0, duration: 0.45, ease: 'power3.in' }),
            );
        }
    });
}
